import React, { useCallback, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import LoginIcon from '@mui/icons-material/Login'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTheme } from '@mui/material/styles'
import { login as loginSsoApi, refreshSsoSession } from '@/apis/userAuthApi'
import { tokenStorage, profileStorage } from '@/services/tokenStorage'
import { getAuthLoginData, extractTokensFromPayload } from './loginPayload'
import { decodeJwtPayload } from '@/utils/jwtUtils'
import { uiPalette } from '@/theme/uiPalette'
import BanhangLogo from './BanhangLogo'

type LoginFields = {
  username: string
  password: string
  showPassword: boolean
}

const initialFields = (): LoginFields => ({
  username: '',
  password: '',
  showPassword: false,
})

const resolveRedirect = (state: unknown): string => {
  const from = (state as { from?: { pathname?: string } })?.from?.pathname
  return from && from !== '/login' ? from : '/dashboard'
}

const resolveErrorMessage = (err: unknown, fallback: string): string => {
  const e = err as { message?: string; error?: string }
  return e?.message ?? e?.error ?? fallback
}

type SignInFormProps = {
  /** Có thì gọi thay cho điều hướng sau khi đăng nhập (vd. modal hết phiên). */
  onSuccess?: () => void
}

const SignInForm: React.FC<SignInFormProps> = ({ onSuccess }) => {
  const theme = useTheme()
  const navigate = useNavigate()
  const location = useLocation()
  const redirectTo = resolveRedirect(location.state)

  const primary = theme.palette.primary.main
  const primaryDark = theme.palette.primary.dark
  const primaryLight = theme.palette.primary.light
  const surface = theme.palette.banhang?.primarySurface ?? uiPalette.primary.surface
  const surfaceBorder = theme.palette.banhang?.primarySurfaceBorder ?? uiPalette.primary.surfaceBorder

  const [fields, setFields] = useState<LoginFields>(initialFields)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const patchFields = useCallback((patch: Partial<LoginFields>) => {
    setFields((prev) => ({ ...prev, ...patch }))
    setError('')
  }, [])

  // Giống hệt SignInForm SX; chỉ dùng px cho borderRadius vì theme BH shape=4
  const fieldSx = useMemo(
    () => ({
      '& .MuiInputLabel-root': {
        fontWeight: 600,
        color: uiPalette.neutral.textMuted,
        fontSize: '1.05rem',
      },
      '& .MuiInputLabel-shrink': {
        fontSize: '0.95rem',
      },
      '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        backgroundColor: '#fff',
        '& fieldset': { borderColor: surfaceBorder },
        '&:hover fieldset': { borderColor: primary },
        '&.Mui-focused fieldset': { borderColor: primary, borderWidth: 2 },
      },
      '& .MuiInputBase-input': { fontSize: '1.05rem', padding: '8.5px 14px' },
    }),
    [primary, surfaceBorder],
  )

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    const username = fields.username.trim()
    if (!username || !fields.password) {
      setError('Vui lòng nhập tên đăng nhập và mật khẩu')
      return
    }

    setLoading(true)
    setError('')
    try {
      const res = await loginSsoApi(username, fields.password)
      let d = getAuthLoginData(res)
      const { accessToken: accessFromLogin } = extractTokensFromPayload(d)
      const accessLogin = (accessFromLogin ?? '').trim()
      if (!accessLogin) {
        setError('Đăng nhập thất bại: thiếu access token')
        return
      }
      tokenStorage.setTokens(accessLogin, null, 'user')
      try {
        const refreshRes = await refreshSsoSession()
        d = getAuthLoginData(refreshRes)
      } catch (refreshErr: unknown) {
        tokenStorage.clearTokens()
        setError(resolveErrorMessage(refreshErr, 'Làm mới phiên đăng nhập thất bại'))
        return
      }
      const { accessToken: accessSaved, refreshToken: refreshSaved } = extractTokensFromPayload(d)
      const token = (accessSaved ?? '').trim()
      if (!token) {
        tokenStorage.clearTokens()
        setError('Làm mới phiên đăng nhập thất bại: thiếu access token')
        return
      }
      tokenStorage.setTokens(token, refreshSaved ?? null, 'user')
      const jwt = decodeJwtPayload(token)
      const roleFromJwt = typeof jwt?.role === 'string' ? jwt.role : undefined
      const ui = d.userInfo
      profileStorage.setProfile({
        ...(ui && typeof ui === 'object' && ui !== null ? (ui as Record<string, unknown>) : {}),
        duAn: d.duAn,
        chiNhanh: d.chiNhanh,
        type: 'user',
        ...(roleFromJwt ? { role: roleFromJwt } : {}),
      })
      if (onSuccess) {
        onSuccess()
        return
      }
      navigate(redirectTo, { replace: true })
    } catch (err: unknown) {
      setError(resolveErrorMessage(err, 'Đăng nhập thất bại'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <Card
      elevation={0}
      sx={{
        position: 'relative',
        width: '100%',
        maxWidth: 390,
        overflow: 'hidden',
        borderRadius: 4,
        border: `1px solid ${surfaceBorder}`,
        bgcolor: '#ffffff',
        boxShadow: `0 24px 56px ${primaryDark}55`,
        transition: 'border-color 220ms ease, box-shadow 220ms ease',
      }}
    >
      <Box
        sx={{
          px: 2.5,
          pt: 2.4,
          pb: 1.8,
          textAlign: 'center',
          borderBottom: `1px solid ${surfaceBorder}`,
          background: `radial-gradient(circle at 15% 10%, ${primary}22, transparent 36%), linear-gradient(170deg, ${surface}, #ffffff)`,
          transition: 'background 220ms ease, border-color 220ms ease',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.1,
            mb: 0.4,
          }}
        >
          <BanhangLogo size={56} />
          <Typography
            component="h1"
            variant="h4"
            fontWeight={900}
            sx={{
              lineHeight: 1.1,
              letterSpacing: 0.2,
              fontSize: { xs: '2rem', sm: '2.15rem' },
              background: `linear-gradient(90deg, ${primaryDark}, ${primary} 58%, ${primaryLight})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Lê Trần
          </Typography>
        </Box>
        <Typography
          color={uiPalette.neutral.textMuted}
          fontWeight={700}
          sx={{ mt: 0.7, fontSize: '1.1rem' }}
        >
          Nhân viên · Hệ thống bán hàng
        </Typography>
      </Box>

      <Box component="form" onSubmit={onSubmit} noValidate>
        <CardContent sx={{ px: 3.4, pt: 3, pb: 2 }}>
          {error ? (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              {error}
            </Alert>
          ) : null}
          <TextField
            label="Tên đăng nhập"
            fullWidth
            margin="normal"
            value={fields.username}
            onChange={(event) => patchFields({ username: event.target.value })}
            disabled={loading}
            autoComplete="username"
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonOutlineIcon sx={{ color: primary }} />
                </InputAdornment>
              ),
            }}
            sx={fieldSx}
          />
          <TextField
            label="Mật khẩu"
            type={fields.showPassword ? 'text' : 'password'}
            fullWidth
            margin="normal"
            value={fields.password}
            onChange={(event) => patchFields({ password: event.target.value })}
            disabled={loading}
            autoComplete="current-password"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlinedIcon sx={{ color: primary }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={fields.showPassword ? 'Ẩn mật khẩu' : 'Hiện mật khẩu'}
                    onClick={() => patchFields({ showPassword: !fields.showPassword })}
                    edge="end"
                    disabled={loading}
                    size="small"
                    sx={{ color: primary }}
                  >
                    {fields.showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={fieldSx}
          />
        </CardContent>
        <CardActions sx={{ px: 3.4, pb: 3, pt: 0.5, justifyContent: 'center' }}>
          <Button
            variant="contained"
            type="submit"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <LoginIcon />}
            size="large"
            sx={{
              width: '68%',
              py: 1.05,
              borderRadius: 2,
              fontWeight: 800,
              letterSpacing: 0.3,
              textTransform: 'none',
              background: `linear-gradient(100deg, ${primaryDark}, ${primary} 52%, ${primaryLight})`,
              boxShadow: `0 9px 20px ${primary}52`,
              '&:hover': {
                transform: 'translateY(-1px)',
                background: `linear-gradient(100deg, ${primaryDark}, ${primary} 60%, ${primary})`,
                boxShadow: `0 13px 26px ${primary}66`,
              },
            }}
          >
            {loading ? 'Đang đăng nhập...' : 'Đăng nhập'}
          </Button>
        </CardActions>
      </Box>
    </Card>
  )
}

export default SignInForm
